import { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useText } from '../context/TextContext'
import PageHeader from '../components/PageHeader'
import { AdvancedButton } from '../components/AdvancedButton'
import DealModal from '../components/modals/DealModal'
import { useFirebaseDoc } from '../hooks/firebaseHooks'
import { Deal } from '../models/shared/Deal'
import { Invoice } from '../models/shared/Invoice'
import { COLLECTIONS } from '../utils/shared/constants'
import { useOrganization } from '../context/OrganizationContext'
import InvoiceEditor from '../components/invoices/InvoiceEditor'
import InvoiceShareModal from '../components/modals/InvoiceShareModal'
import { Customer } from '../models/shared/Customer'
import { ShareToken } from '../models/shared/ShareToken'
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore'
import { useFirestore } from 'reactfire'
import { createShareTokenLink } from '../utils/shared/links'

export default function InvoiceEditorPage() {
    const { dealId, invoiceId } = useParams<{ dealId: string; invoiceId: string }>()
    const { orgId } = useOrganization();
    const text = useText()
    const firestore = useFirestore();
    const [showDeal, setShowDeal] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)
    const [deal] = useFirebaseDoc<Deal>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}`);
    const [invoice] = useFirebaseDoc<Invoice>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}/${COLLECTIONS.INVOICES}/${invoiceId}`);
    const [customer] = useFirebaseDoc<Customer>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.CUSTOMERS}/${deal?.customerId || ''}`);

    useEffect(() => {
        if (!invoice || invoice.shareTokenId) return;

        const createShareTokenAsync = async () => {
            const newShareToken: ShareToken = {
                orgId,
                dealId: dealId || '',
                invoiceId: invoiceId || '',
            };
            const newTokenDoc = await addDoc(collection(firestore, COLLECTIONS.SHARE_TOKENS), newShareToken);

            await updateDoc(
                doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${dealId}/${COLLECTIONS.INVOICES}/${invoiceId}`),
                { shareTokenId: newTokenDoc.id });
        }

        createShareTokenAsync();
    }, [invoice, dealId, invoiceId, orgId, firestore]);

    if (!deal || !invoice) {
        return <Spinner animation='border' className='m-5' />
    }

    return (
        <>
            <PageHeader
                title={text.invoice}
                showBackButton
                ignoreEscape
                actionsPrefix={
                    <AdvancedButton icon='bi-pencil' variant='outline-secondary'
                        onClick={() => setShowDeal(!showDeal)}
                    >{text.deal}</AdvancedButton>
                }
                actions={[
                    {
                        text: text.send,
                        icon: 'bi-send',
                        onClick: () => setShowShareModal(true),
                    },
                    {
                        text: text.preview,
                        icon: 'bi-box-arrow-up-right',
                        link: createShareTokenLink(invoice.shareTokenId || '', { invoice: true }),
                        newTabLink: true,
                    },
                ]}
            />

            <InvoiceEditor deal={deal} invoice={invoice} />

            {showDeal && <DealModal deal={deal} onClose={() => setShowDeal(false)} />}

            <InvoiceShareModal customer={customer} invoice={showShareModal ? invoice : null} onHide={() => setShowShareModal(false)} />
        </>
    )
}
